import {
  faClock,
  faCrown,
  faExclamationTriangle,
  faFutbol,
  faBasketball,
  faHandshakeAngle,
  faInfoCircle,
  faRotateLeft,
  faTableTennisPaddleBall,
  faMinus,
  faPen,
  faPlus,
  faPrint,
  faSave,
  faTowerBroadcast,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Card,
  Checkbox,
  Empty,
  Flex,
  Input,
  InputNumber,
  Modal as AntModal,
  Segmented,
  Tooltip,
} from "antd";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { TailSpin } from "react-loader-spinner";
import { Link, useParams } from "react-router-dom";
import {
  AdminFootballTeams,
  BestExtraMode,
  BracketMatch,
  FootballGoal,
  FullBracket,
  GironeDay,
  LabeledGirone,
  PartialPair,
  TopAssist,
  TopScorer,
  Tournaments,
} from "../../api/Tournaments";
import BracketItem, { BracketSlot } from "../../Components/BracketItem";
import { useToasts } from "../../contexts/toasts";
import { PadelScore, WinnerTeam } from "../../types/Result";
import {
  FootballTeam,
  StandingsCriteria,
  TournamentBracket,
} from "../../types/Tournaments";
import { TournamentFormula } from "./GenerateSchedule";
import {
  computeAmericanoStandings,
  computeGironeStandings,
  ShootoutPoints,
} from "../../utils/standings";
import classNames from "classnames";
import "./TournamentBrackets.css";

/** Identificativo stabile di un giocatore in rosa (userId oppure ospite per indice). */
const memberPlayerId = (
  member: FootballTeam["members"][number],
  teamId: string,
  index: number
): string => member.userId || `guest-${teamId}-${index}`;

// Quante coppie/squadre per girone sono qualificate al Gold, ricavato dal
// tabellone Gold (segnaposto `G<girone>-R<n>` del primo turno). Serve per i
// tabelloni salvati prima della colonna `coppieGold`.
// Impostazioni del Gold con ripescaggio (teste di serie globali), dai
// segnaposto `BEST-R<rank>-<i>`: le fasce complete (una voce per girone) sono
// le qualificate di diritto, la fascia parziale più alta sono le ripescate.
const inferGoldSettings = (
  goldBracket: FullBracket | null | undefined,
  gironiCount: number
): {
  coppieGold: number;
  bestExtra: number;
  bestExtraMode: BestExtraMode;
} | null => {
  const first = goldBracket?.[0];
  if (!first) return null;
  const rankOf = (entry: BracketMatch["home"]) => {
    if (!entry || entry === "BYE") return undefined;
    const found = /^BEST-R(\d+)-(\d+)$/.exec((entry as PartialPair).id || "");
    return found ? parseInt(found[1], 10) : undefined;
  };
  const perRank = new Map<number, number>();
  for (const m of first.matches) {
    for (const entry of [m.home, m.away]) {
      const rank = rankOf(entry);
      if (rank !== undefined) perRank.set(rank, (perRank.get(rank) || 0) + 1);
    }
  }
  if (perRank.size === 0) return null;
  let coppieGold = 0;
  let bestExtra = 0;
  Array.from(perRank.entries())
    .sort((a, b) => a[0] - b[0])
    .forEach(([rank, count]) => {
      if (gironiCount > 0 && count >= gironiCount) coppieGold = rank;
      else bestExtra = count;
    });
  // Due ripescate una contro l'altra al primo turno = spareggio tra loro.
  const among = first.matches.some(
    (m) => rankOf(m.home) === coppieGold + 1 && rankOf(m.away) === coppieGold + 1
  );
  return {
    coppieGold,
    bestExtra,
    bestExtraMode: among ? "amongThemselves" : "vsSeeds",
  };
};

const inferCoppieGold = (
  goldBracket: FullBracket | null | undefined,
  gironiCount: number,
  bestExtra = 0
): number => {
  const first = goldBracket?.[0];
  if (!first) return 0;
  let max = 0;
  let real = 0;
  for (const m of first.matches) {
    for (const entry of [m.home, m.away]) {
      if (!entry || entry === "BYE") continue;
      const p = entry as PartialPair;
      if (p.player1Name === "Bye" || (p.id || "").startsWith("BYE")) continue;
      real++;
      const found = /^G[^-]+-R(\d+)$/.exec(p.id || "");
      if (found) max = Math.max(max, parseInt(found[1], 10));
    }
  }
  if (max > 0) return max;
  const global = inferGoldSettings(goldBracket, gironiCount);
  if (global && global.coppieGold > 0) return global.coppieGold;
  // Tabellone già popolato (segnaposto sostituiti dalle squadre reali):
  // qualificate = posti occupati nel primo turno (meno le ripescate) diviso i gironi.
  return gironiCount > 0
    ? Math.max(1, Math.ceil((real - bestExtra) / gironiCount))
    : 0;
};

const memberFullName = (member: FootballTeam["members"][number]): string =>
  `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim();

// Nome del turno della finale 3°/4° posto (deve combaciare con
// THIRD_PLACE_ROUND_NAME del backend): è appeso in coda ai turni ma non è la
// finale, quindi va escluso quando si cerca il campione.
const THIRD_PLACE_ROUND_NAME = "Finale 3°/4° posto";

// La vera finale di un tabellone: ultima partita ESCLUSO il turno per il 3°/4°
// posto (che è appeso in coda ai turni ma non assegna il titolo).
const finalMatchOf = (
  bracket: { name: string; matches: BracketMatch[] }[] | undefined | null
): BracketMatch | undefined => {
  if (!bracket) return undefined;
  const matches = bracket
    .filter((round) => round.name !== THIRD_PLACE_ROUND_NAME)
    .flatMap((round) => round.matches);
  return matches[matches.length - 1];
};

// Vincitore della vera finale (il campione).
const finalWinnerOf = (
  bracket: { name: string; matches: BracketMatch[] }[] | undefined | null
): PartialPair | undefined => {
  const lastMatch = finalMatchOf(bracket);
  if (!lastMatch) return undefined;
  if (lastMatch.winnerTeam === WinnerTeam.Team1)
    return lastMatch.home as PartialPair;
  if (lastMatch.winnerTeam === WinnerTeam.Team2)
    return lastMatch.away as PartialPair;
  return undefined;
};

/**
 * Formatta il nome di una coppia/squadra.
 * - Padel: "Giocatore1 / Giocatore2"
 * - Calcio: solo il nome della squadra (player1Name), senza "/".
 */
const formatPair = (pair?: PartialPair | "BYE" | string): string => {
  if (!pair) return "—";
  if (pair === "BYE") return "BYE";
  if (typeof pair === "string") return pair;
  return pair.player2Name
    ? `${pair.player1Name} / ${pair.player2Name}`
    : pair.player1Name;
};

/** Formatta la data/ora partita per la visualizzazione (es. "11/07 · 15:30"). */
/** Etichetta per le partite decise ai calci di rigore (es. "d.c.r. 5-4"). */
const shootoutLabel = (match: BracketMatch): string | undefined =>
  match.shootout && match.shootout.team1 !== match.shootout.team2
    ? `Dopo i calci di rigore (${match.shootout.team1}-${match.shootout.team2})`
    : undefined;

const formatMatchDateTime = (dt?: string): string => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => `${n}`.padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} · ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/** Iniziale della squadra/coppia, per l'avatar dello scoreboard. */
const teamInitials = (pair?: PartialPair | "BYE"): string => {
  if (!pair || pair === "BYE") return "?";
  return pair.player1Name?.trim()?.charAt(0)?.toUpperCase() || "?";
};

function RankingCard({
  title,
  icon,
  rows,
  teamName,
}: {
  title: string;
  icon: typeof faFutbol;
  rows: { playerName: string; teamId: string; value: number }[];
  teamName: (teamId: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 5);
  return (
    <Col xs={12} md={6} className="mb-3">
      <div className="ranking-card">
        <div className="ranking-card__head">
          <FontAwesomeIcon icon={icon} className="me-2" />
          {title}
        </div>
        {rows.length === 0 ? (
          <div className="ranking-empty">Nessun dato</div>
        ) : (
          visible.map((r, i) => (
            <div className="ranking-row" key={i}>
              <span
                className={classNames("ranking-pos", {
                  "ranking-pos--top": i < 3,
                })}
              >
                {i + 1}
              </span>
              <span className="ranking-name">
                {r.playerName || "—"}
                {teamName(r.teamId) && (
                  <small className="ranking-team"> · {teamName(r.teamId)}</small>
                )}
              </span>
              <span className="ranking-value">{r.value}</span>
            </div>
          ))
        )}
        {rows.length > 5 && (
          <button
            type="button"
            className="ranking-more"
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? "Mostra meno" : `Mostra di più (${rows.length - 5})`}
          </button>
        )}
      </div>
    </Col>
  );
}

function ChampionBanner({
  winner,
  isFootball,
}: {
  winner: PartialPair;
  isFootball?: boolean;
}) {
  return (
    <div className="champion-banner">
      <FontAwesomeIcon icon={faCrown} className="champion-crown" />
      <div className="champion-text">
        <span className="champion-label">
          {isFootball ? "Campione" : "Campioni"}
        </span>
        <span className="champion-name">{formatPair(winner)}</span>
      </div>
    </div>
  );
}

export default function TournamentBrackets() {
  const { id } = useParams();
  const { showToast } = useToasts();
  const [tabellone, setTabellone] = useState<TournamentBracket>();
  const [match, setMatch] = useState<BracketMatch>();
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [error, setError] = useState(false);
  const [winner, setWinner] = useState<WinnerTeam>();
  const [liveScore, setLiveScore] = useState<
    {
      team1?: string;
      team2?: string;
    }[]
  >([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [girone, setGirone] = useState<string>();
  const [gold, setGold] = useState(false);
  const [silver, setSilver] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [goldWinner, setGoldWinner] = useState<PartialPair>();
  const [silverWinner, setSilverWinner] = useState<PartialPair>();
  const [roundWinner, setRoundWinner] = useState<PartialPair>();
  const [scorers, setScorers] = useState<FootballGoal[]>([]);
  // Rigori della partita in modifica (stringhe: input controllati).
  const [shootoutScore, setShootoutScore] = useState<{
    team1: string;
    team2: string;
  }>({ team1: "", team2: "" });
  const [matchDateTime, setMatchDateTime] = useState<string>("");
  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>([]);
  const [topScorers, setTopScorers] = useState<TopScorer[]>([]);
  const [topAssists, setTopAssists] = useState<TopAssist[]>([]);

  // Basket (sportId 4): stessa struttura del calcio (squadre, un solo "set")
  // ma con i punti al posto dei gol, senza rigori/assist e senza pareggi.
  const isBasket = tabellone?.tournament?.sportId === 4;
  const isFootball = tabellone?.tournament?.sportId === 3 || isBasket;
  // Americano: classifica per singolo giocatore (calcolata dai punteggi grezzi).
  const isAmericano = tabellone?.formula === TournamentFormula.Americano;

  // Criterio della classifica (differenza vs scontri diretti). Deriva dal
  // tabellone salvato; default "difference" per retrocompatibilità.
  const standingsCriteria: StandingsCriteria =
    tabellone?.standingsCriteria === "headToHead" ? "headToHead" : "difference";
  const [criteriaLoading, setCriteriaLoading] = useState(false);

  // Impostazioni della fase finale (modificabili a gironi già in corso).
  const [finalPhase, setFinalPhase] = useState<{
    coppieGold: number;
    silver: boolean;
    thirdPlace: boolean;
    // Ripescaggio: n migliori (coppieGold+1)-esime nel Gold.
    bestExtra: number;
    bestExtraMode: BestExtraMode;
  }>({
    coppieGold: 1,
    silver: false,
    thirdPlace: false,
    bestExtra: 0,
    bestExtraMode: "vsSeeds",
  });
  const [finalPhaseLoading, setFinalPhaseLoading] = useState(false);
  // Valori correnti letti dal tabellone salvato (per capire cosa è cambiato).
  const savedFinalPhase = useMemo(() => {
    const inferred = inferGoldSettings(
      tabellone?.goldBracket,
      (tabellone?.roundRobin?.gironi || []).length
    );
    const bestExtra = tabellone?.bestExtra ?? inferred?.bestExtra ?? 0;
    const bestExtraMode: BestExtraMode =
      tabellone?.bestExtraMode ?? inferred?.bestExtraMode ?? "vsSeeds";
    return {
      coppieGold:
        tabellone?.coppieGold ||
        inferCoppieGold(
          tabellone?.goldBracket,
          (tabellone?.roundRobin?.gironi || []).length,
          bestExtra
        ) ||
        1,
      silver: !!tabellone?.silverBracket?.length,
      thirdPlace: !!tabellone?.goldBracket?.some(
        (r) => r.name === THIRD_PLACE_ROUND_NAME
      ),
      bestExtra,
      bestExtraMode,
    };
  },
    // Dipendenze puntuali: gli aggiornamenti ottimistici di altre impostazioni
    // (criterio, rigori) non devono azzerare le modifiche non ancora salvate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tabellone?.coppieGold,
      tabellone?.bestExtra,
      tabellone?.bestExtraMode,
      tabellone?.goldBracket,
      tabellone?.silverBracket,
      tabellone?.roundRobin,
    ]
  );
  useEffect(() => {
    setFinalPhase(savedFinalPhase);
  }, [savedFinalPhase]);
  // Partecipanti nel girone più piccolo: limite per le qualificate al Gold.
  const minPerGirone = useMemo(() => {
    const gironi = tabellone?.roundRobin?.gironi || [];
    if (!gironi.length) return 0;
    return Math.min(...gironi.map((g) => g.pairs.length));
  }, [tabellone]);
  const gironiCount = (tabellone?.roundRobin?.gironi || []).length;
  // Ripescaggio possibile solo senza Silver, con più gironi e se esiste una
  // (coppieGold+1)-esima in ogni girone.
  const bestExtraAvailable =
    !finalPhase.silver &&
    gironiCount > 1 &&
    finalPhase.coppieGold < minPerGirone;
  const effectiveBestExtra = bestExtraAvailable ? finalPhase.bestExtra : 0;
  // Qualificate al Gold in totale e posti del tabellone (potenza di 2): se non
  // coincidono alcune squadre passano il primo turno con un BYE.
  const goldQualified = finalPhase.coppieGold * gironiCount + effectiveBestExtra;
  const goldSpots =
    goldQualified > 0
      ? Math.pow(2, Math.ceil(Math.log2(Math.max(2, goldQualified))))
      : 0;
  const finalPhaseDirty =
    finalPhase.coppieGold !== savedFinalPhase.coppieGold ||
    finalPhase.silver !== savedFinalPhase.silver ||
    finalPhase.thirdPlace !== savedFinalPhase.thirdPlace ||
    effectiveBestExtra !== savedFinalPhase.bestExtra ||
    (effectiveBestExtra > 0 &&
      finalPhase.bestExtraMode !== savedFinalPhase.bestExtraMode);

  // Rigori nei pareggi dei gironi (solo calcio): impostazioni del tabellone.
  const shootoutEnabled = !!tabellone?.shootout;
  const shootoutWinPoints = tabellone?.shootoutWinPoints ?? 2;
  const shootoutLosePoints = tabellone?.shootoutLosePoints ?? 1;
  const shootoutPoints = useMemo<ShootoutPoints | undefined>(
    () =>
      shootoutEnabled
        ? { win: shootoutWinPoints, lose: shootoutLosePoints }
        : undefined,
    [shootoutEnabled, shootoutWinPoints, shootoutLosePoints]
  );

  // Una partita di calcio finita in parità può andare ai rigori: nei gironi
  // solo se l'opzione è attiva, nella fase finale sempre (serve un vincitore
  // per far avanzare il tabellone).
  const shootoutAvailable = useMemo(() => {
    if (!isFootball || isBasket) return false;
    const g1 = (liveScore || [])[0]?.team1;
    const g2 = (liveScore || [])[0]?.team2;
    // Lo 0-0 arriva qui come stringa vuota (l'input mostra "" per lo zero):
    // il blocco va comunque mostrato se la partita è stata giocata o se si sta
    // digitando un risultato, ma non su una partita ancora vuota.
    const empty = (v?: string) => v === undefined || v === "";
    if (empty(g1) && empty(g2) && !(match?.score || []).length) return false;
    if (Number(g1 || 0) !== Number(g2 || 0)) return false;
    return girone ? shootoutEnabled : true;
  }, [isFootball, isBasket, liveScore, girone, shootoutEnabled, match]);

  // Cambia il criterio: aggiorno subito la classifica (ottimistico) e persisto.
  const handleStandingsCriteriaChange = useCallback(
    (next: StandingsCriteria) => {
      if (!id) return;
      const prevCriteria =
        tabellone?.standingsCriteria === "headToHead"
          ? "headToHead"
          : "difference";
      if (next === prevCriteria) return;
      setCriteriaLoading(true);
      setTabellone((prev) =>
        prev ? { ...prev, standingsCriteria: next } : prev
      );
      Tournaments.updateBracketSettings(id, next).then(
        () => setCriteriaLoading(false),
        () => {
          setCriteriaLoading(false);
          setTabellone((prev) =>
            prev ? { ...prev, standingsCriteria: prevCriteria } : prev
          );
          showToast(
            "criteria-error",
            "Impossibile aggiornare il criterio della classifica",
            "",
            "danger"
          );
        }
      );
    },
    [id, tabellone, showToast]
  );

  // Cambia le impostazioni dei rigori su un tabellone già generato: aggiorno
  // subito (ottimistico) e persisto insieme al criterio corrente.
  const handleShootoutSettingsChange = useCallback(
    (next: { shootout: boolean; win: number; lose: number }) => {
      if (!id) return;
      const prev = {
        shootout: !!tabellone?.shootout,
        win: tabellone?.shootoutWinPoints ?? 2,
        lose: tabellone?.shootoutLosePoints ?? 1,
      };
      setCriteriaLoading(true);
      setTabellone((p) =>
        p
          ? {
              ...p,
              shootout: next.shootout,
              shootoutWinPoints: next.win,
              shootoutLosePoints: next.lose,
            }
          : p
      );
      Tournaments.updateBracketSettings(id, standingsCriteria, {
        shootout: next.shootout,
        shootoutWinPoints: next.win,
        shootoutLosePoints: next.lose,
      }).then(
        () => setCriteriaLoading(false),
        () => {
          setCriteriaLoading(false);
          setTabellone((p) =>
            p
              ? {
                  ...p,
                  shootout: prev.shootout,
                  shootoutWinPoints: prev.win,
                  shootoutLosePoints: prev.lose,
                }
              : p
          );
          showToast(
            "shootout-error",
            "Impossibile aggiornare le impostazioni dei rigori",
            "",
            "danger"
          );
        }
      );
    },
    [id, tabellone, standingsCriteria, showToast]
  );

  // Mappa teamId -> rosa, per popolare il selettore dei marcatori.
  const rosterByTeamId = useMemo(() => {
    const map: Record<string, FootballTeam> = {};
    footballTeams.forEach((team) => {
      map[team.id] = team;
    });
    return map;
  }, [footballTeams]);
  // Fallback per nome: i tabelloni salvati con la vecchia logica potevano avere
  // l'id della squadra sovrascritto dal segnaposto `Winner-…` quando avanzava di
  // turno; in quel caso si risolve la rosa dal nome (che resta invariato).
  const rosterByName = useMemo(() => {
    const map: Record<string, FootballTeam> = {};
    footballTeams.forEach((team) => {
      if (team.name) map[team.name] = team;
    });
    return map;
  }, [footballTeams]);

  const loadRankings = useCallback(() => {
    if (!id) return;
    Tournaments.getTopScorers(id).then(
      ({ data }) => setTopScorers(data.scorers || []),
      () => setTopScorers([])
    );
    Tournaments.getTopAssists(id).then(
      ({ data }) => setTopAssists(data.assists || []),
      () => setTopAssists([])
    );
  }, [id]);

  useEffect(() => {
    if (id && isFootball) {
      AdminFootballTeams.getTeams(id).then(
        ({ data }) => setFootballTeams(data.teams || []),
        () => setFootballTeams([])
      );
      loadRankings();
    }
  }, [id, isFootball, loadRankings]);

  const handleDownloadPdf = useCallback(async () => {
    if (!ref.current) return;

    // Cattura il DOM come canvas
    const canvas = await html2canvas(ref.current, {
      scale: 2, // aumenta la risoluzione
      useCORS: true, // per immagini esterne
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${tabellone?.tournament.name}.pdf`);
  }, [tabellone]);

  const handleMatchModalClose = useCallback(() => {
    setMatchModalOpen(false);
    setMatch(undefined);
    setLiveScore([]);
    setWinner(undefined);
    setGirone(undefined);
    setGold(false);
    setSilver(false);
    setScorers([]);
    setShootoutScore({ team1: "", team2: "" });
    setMatchDateTime("");
  }, []);

  const handleMatchModalOpen = useCallback(
    (selectedMatch: BracketMatch, girone?: string) => () => {
      setMatch(selectedMatch);
      setMatchModalOpen(true);
      const existingScore = (selectedMatch.score || []).map((sc) => ({
        team1: sc.team1 ? sc.team1.toString() : undefined,
        team2: sc.team2 ? sc.team2.toString() : undefined,
      }));
      setLiveScore(existingScore.length > 0 ? existingScore : [{ team1: "", team2: "" }]);
      setScorers(selectedMatch.scorers || []);
      setShootoutScore({
        team1: selectedMatch.shootout ? `${selectedMatch.shootout.team1}` : "",
        team2: selectedMatch.shootout ? `${selectedMatch.shootout.team2}` : "",
      });
      setMatchDateTime(selectedMatch.dateTime ? selectedMatch.dateTime.slice(0, 16) : "");
      if (girone) setGirone(girone);
    },
    []
  );

  const openMatchModalFromClick = useCallback(
    (
      selectedMatch: BracketMatch,
      girone?: string,
      g?: boolean,
      s?: boolean
    ) => {
      setMatch(selectedMatch);
      setMatchModalOpen(true);
      const existingScore = (selectedMatch.score || []).map((sc) => ({
        team1: sc.team1 ? sc.team1.toString() : undefined,
        team2: sc.team2 ? sc.team2.toString() : undefined,
      }));
      setLiveScore(existingScore.length > 0 ? existingScore : [{ team1: "", team2: "" }]);
      setScorers(selectedMatch.scorers || []);
      setShootoutScore({
        team1: selectedMatch.shootout ? `${selectedMatch.shootout.team1}` : "",
        team2: selectedMatch.shootout ? `${selectedMatch.shootout.team2}` : "",
      });
      setMatchDateTime(selectedMatch.dateTime ? selectedMatch.dateTime.slice(0, 16) : "");
      if (girone) setGirone(girone);
      setGold(g || false);
      setSilver(s || false);
    },
    []
  );

  const handleBracketModalOpen = useCallback(
    (matchId: string, gold?: boolean, silver?: boolean) => {
      const match = gold
        ? tabellone!
            .goldBracket!.flatMap((tab) => tab.matches)
            .find((match) => match.id === matchId)
        : silver
        ? tabellone!
            .silverBracket!.flatMap((tab) => tab.matches)
            .find((match) => match.id === matchId)
        : tabellone!
            .rounds!.flatMap((tab) => tab.matches)
            .find((match) => match.id === matchId);

      if (match) openMatchModalFromClick(match, undefined, gold, silver);
    },
    [tabellone, openMatchModalFromClick]
  );

  const initTabellone = useCallback(() => {
    Tournaments.getTabellone(id!).then(
      ({ data }) => setTabellone(data),
      () => {
        showToast(
          "tournament-tabellone-error",
          "Impossibile ottenere il tabellone del torneo",
          "",
          "danger"
        );
        setError(true);
      }
    );
  }, [showToast, id]);

  const handleReset = useCallback(() => {
    handleMatchModalClose();
    setTabellone(undefined);
    initTabellone();
    if (isFootball) loadRankings();
  }, [handleMatchModalClose, initTabellone, isFootball, loadRankings]);

  // Drag & drop nel primo turno: scambia due voci fra due partite (solo fra
  // partite non ancora giocate; il backend ricalcola BYE e turni successivi).
  const handleSwapEntries = useCallback(
    (bracket: "gold" | "silver" | "rounds") =>
      (from: BracketSlot, to: BracketSlot) => {
        if (!id) return;
        setResultLoading(true);
        Tournaments.swapBracketEntries(id, {
          bracket,
          fromMatchId: from.matchId,
          fromSide: from.side,
          toMatchId: to.matchId,
          toSide: to.side,
        }).then(
          () => {
            setResultLoading(false);
            handleReset();
            showToast("swap-success", "Coppie scambiate", "", "success");
          },
          (err: any) => {
            setResultLoading(false);
            showToast(
              "swap-error",
              "Impossibile scambiare le coppie",
              err?.response?.data?.message || "",
              "danger"
            );
          }
        );
      },
    [id, handleReset, showToast]
  );


  useEffect(() => {
    if (id) initTabellone();
  }, [id, initTabellone]);

  const isRiposoMatch = useCallback((fixture: BracketMatch[]) => {
    let restMatch: string = "";
    fixture.forEach((match) => {
      if (match.home !== "BYE") {
        //Controllo match.away
        if (match.away !== "BYE") {
          if (
            match.away.player1Name === "Riposo" ||
            match.away.player2Name === "Riposo"
          )
            restMatch = formatPair(match.home);
          else if (
            match.home.player1Name === "Riposo" ||
            match.home.player2Name === "Riposo"
          )
            restMatch = formatPair(match.away);
        }
      }
    });
    return restMatch;
  }, []);

  const parseGoldBracket = useMemo(() => {
    if (!tabellone || !tabellone.goldBracket) return [];
    const brackets = tabellone;
    return (brackets!.goldBracket || []).map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: m.id,
        teams: [{ name: formatPair(m.home) }, { name: formatPair(m.away) }],
        score: m.score,
        winnerTeam: m.winnerTeam,
        live: m.live,
        dateTime: m.dateTime,
        shootoutLabel: shootoutLabel(m),
      })),
    }));
  }, [tabellone]);

  const parseSilverBracket = useMemo(() => {
    if (!tabellone || !tabellone.silverBracket) return [];
    const brackets = tabellone;
    return brackets!.silverBracket?.map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: m.id,
        teams: [{ name: formatPair(m.home) }, { name: formatPair(m.away) }],
        score: m.score,
        winnerTeam: m.winnerTeam,
        live: m.live,
        dateTime: m.dateTime,
        shootoutLabel: shootoutLabel(m),
      })),
    }));
  }, [tabellone]);

  const parseDirectBracket = useMemo(() => {
    if (!tabellone || !tabellone.rounds) return;
    const brackets = tabellone;
    return brackets.rounds?.map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: m.id,
        teams: [{ name: formatPair(m.home) }, { name: formatPair(m.away) }],
        score: m.score,
        winnerTeam: m.winnerTeam,
        live: m.live,
        dateTime: m.dateTime,
        shootoutLabel: shootoutLabel(m),
      })),
    }));
  }, [tabellone]);

  const handleAddSet = useCallback(() => {
    setLiveScore((previous) => {
      return [...previous, { team1: "", team2: "" }];
    });
  }, []);

  const handleRemoveSet = useCallback(() => {
    setLiveScore((previous) => {
      const newItems = [...previous];
      return newItems.slice(0, newItems.length - 1);
    });
  }, []);

  const handlePopulateBracketsFromGironi = useCallback(
    (gold?: boolean, silver?: boolean) => () => {
      setResultLoading(true);
      const roundRobin: LabeledGirone[] = tabellone!.roundRobin?.gironi || [];
      const bracket: FullBracket = gold
        ? tabellone!.goldBracket || []
        : silver
        ? tabellone!.silverBracket || []
        : tabellone!.rounds || [];
      Tournaments.populateBrackets(id!, {
        roundRobin,
        brackets: bracket,
        gold: gold || false,
        silver: silver || false,
      }).then(
        () => {
          setResultLoading(false);
          handleReset();
          showToast(
            "bracket-success",
            "Tabellone correttamente aggiornato",
            "",
            "success"
          );
        },
        () => {
          showToast(
            "bracket-error",
            "Impossibile aggiornare il tabellone del torneo",
            "",
            "danger"
          );
        }
      );
    },
    [tabellone, id, showToast, handleReset]
  );

  // Salva le nuove impostazioni della fase finale: il backend rigenera solo i
  // tabelloni Gold/Silver (gironi, classifiche e calendario restano intatti);
  // i risultati già inseriti nella fase finale vengono azzerati.
  const handleFinalPhaseSave = useCallback(() => {
    if (!id) return;
    const hasFinalResults = [
      ...(tabellone?.goldBracket || []),
      ...(tabellone?.silverBracket || []),
    ].some((r) => r.matches.some((m) => (m.score || []).length > 0));
    AntModal.confirm({
      title: "Rigenerare la fase finale?",
      content: hasFinalResults
        ? "Le classifiche e il calendario dei gironi restano invariati, ma i tabelloni Gold/Silver vengono ricostruiti: i risultati già inseriti nella fase finale andranno persi e il tabellone andrà ripopolato dalla classifica dei gironi."
        : "Le classifiche e il calendario dei gironi restano invariati: vengono ricostruiti solo i tabelloni Gold/Silver, da ripopolare dalla classifica dei gironi.",
      okText: "Rigenera",
      cancelText: "Annulla",
      okButtonProps: { danger: hasFinalResults },
      onOk: () => {
        setFinalPhaseLoading(true);
        return Tournaments.updateFinalPhase(id, {
          coppieGold: finalPhase.coppieGold,
          silver: finalPhase.silver,
          thirdPlace: isFootball ? finalPhase.thirdPlace : undefined,
          bestExtra: effectiveBestExtra,
          bestExtraMode: finalPhase.bestExtraMode,
        }).then(
          () => {
            setFinalPhaseLoading(false);
            handleReset();
            showToast(
              "final-phase-success",
              "Fase finale aggiornata",
              "Ricorda di popolare il tabellone dalla classifica dei gironi.",
              "success"
            );
          },
          (err: any) => {
            setFinalPhaseLoading(false);
            showToast(
              "final-phase-error",
              "Impossibile aggiornare la fase finale",
              err?.response?.data?.message || "",
              "danger"
            );
          }
        );
      },
    });
  }, [
    id,
    tabellone,
    finalPhase,
    effectiveBestExtra,
    isFootball,
    handleReset,
    showToast,
  ]);

  // Ordina le coppie/squadre di un girone secondo il criterio scelto
  // (differenza o scontri diretti) e lo sport. Logica condivisa col backend.
  const sortPairs = useCallback(
    (girone: LabeledGirone) =>
      computeGironeStandings(
        girone,
        standingsCriteria,
        isFootball,
        shootoutPoints
      ),
    [isFootball, standingsCriteria, shootoutPoints]
  );

  const signedCell = useCallback((val: number) => {
    const cls = val > 0 ? "val-pos" : val < 0 ? "val-neg" : undefined;
    return <span className={cls}>{val > 0 ? `+${val}` : val}</span>;
  }, []);

  // Classifica di un girone (calcetto: V/N/P, GF/GS, DR; padel: set e game).
  const renderStandings = useCallback(
    (girone: LabeledGirone) => {
      const pairs = sortPairs(girone);
      const footballCols = ["PG", "V", "N", "P", "GF", "GS", "DR"];
      const basketCols = ["PG", "V", "P", "PF", "PS", "DP"];
      const padelCols = ["SV", "SP", "DS", "GV", "GP", "DG"];
      const cols = isBasket ? basketCols : isFootball ? footballCols : padelCols;
      return (
        <div className="standings-card">
          <div className="standings-card__head">
            <FontAwesomeIcon icon={faTrophy} />
            Girone {girone.name}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="standings-table">
              <thead>
                <tr>
                  <th className="col-name">Squadra</th>
                  <th>Pt</th>
                  {cols.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pairs.length === 0 ? (
                  <tr>
                    <td colSpan={cols.length + 2}>
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={isFootball ? "Nessuna squadra" : "Nessuna coppia"}
                      />
                    </td>
                  </tr>
                ) : (
                  pairs.map((pair, index) => {
                    const gd = (pair.goalsFor || 0) - (pair.goalsAgainst || 0);
                    const ds = (pair.setsWon || 0) - (pair.setsLost || 0);
                    const dg = (pair.gamesWon || 0) - (pair.gamesLost || 0);
                    return (
                      <tr key={pair.id || index}>
                        <td className="col-name">
                          <span
                            className={classNames("pos-badge", {
                              "pos-badge--top": index < 2,
                            })}
                          >
                            {index + 1}
                          </span>
                          {formatPair(pair)}
                        </td>
                        <td className="col-pts">{pair.rank || 0}</td>
                        {isFootball ? (
                          <>
                            <td>{pair.gamesPlayed || 0}</td>
                            <td>{pair.wins || 0}</td>
                            {!isBasket && <td>{pair.draws || 0}</td>}
                            <td>{pair.losses || 0}</td>
                            <td>{pair.goalsFor || 0}</td>
                            <td>{pair.goalsAgainst || 0}</td>
                            <td>{signedCell(gd)}</td>
                          </>
                        ) : (
                          <>
                            <td>{pair.setsWon || 0}</td>
                            <td>{pair.setsLost || 0}</td>
                            <td>{signedCell(ds)}</td>
                            <td>{pair.gamesWon || 0}</td>
                            <td>{pair.gamesLost || 0}</td>
                            <td>{signedCell(dg)}</td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    },
    [isFootball, isBasket, sortPairs, signedCell]
  );

  // Classifica di un torneo Americano: per singolo giocatore, con i punti vinti.
  const renderAmericanoStandings = useCallback(
    (girone: LabeledGirone) => {
      const rows = computeAmericanoStandings(girone, standingsCriteria);
      return (
        <div className="standings-card">
          <div className="standings-card__head">
            <FontAwesomeIcon icon={faTrophy} />
            Classifica
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="standings-table">
              <thead>
                <tr>
                  <th className="col-name">Giocatore</th>
                  <th>Punti</th>
                  <th>Subiti</th>
                  <th>Diff</th>
                  <th>G</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Nessun giocatore"
                      />
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id || index}>
                      <td className="col-name">
                        <span
                          className={classNames("pos-badge", {
                            "pos-badge--top": index < 3,
                          })}
                        >
                          {index + 1}
                        </span>
                        {row.name}
                      </td>
                      <td className="col-pts">{row.points}</td>
                      <td>{row.conceded}</td>
                      <td>{signedCell(row.diff)}</td>
                      <td>{row.played}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    },
    [standingsCriteria, signedCell]
  );

  // Nomi dei marcatori di una squadra in una partita (con minuto/autogol).
  const teamScorers = useCallback(
    (match: BracketMatch, teamId?: string) =>
      (match.scorers || [])
        .filter((s) => (teamId ? s.teamId === teamId : true))
        .map((s, i) =>
          isBasket ? (
            <span key={i} className="match-scorers__item">
              🏀 {s.playerName} {s.points ?? 0} pt
            </span>
          ) : (
            <span key={i} className="match-scorers__item">
              ⚽ {s.playerName}
              {s.minute ? ` ${s.minute}'` : ""}
              {s.autogoal ? " (aut.)" : ""}
              {s.assistPlayerName ? ` 🅰 ${s.assistPlayerName}` : ""}
            </span>
          )
        ),
    [isBasket]
  );

  // Una giornata del calendario, resa come elenco di card partita.
  const renderGiornata = useCallback(
    (giornata: GironeDay, gironeName: string) => {
      const matches = giornata.matches.filter(
        (match) =>
          (match.home as PartialPair).id !== "BYE" &&
          (match.away as PartialPair).id !== "BYE"
      );
      const rest = isRiposoMatch(giornata.matches);
      return (
        <div className="giornata-card" key={`${gironeName}-${giornata.giornata}`}>
          <div className="giornata-card__head">
            {giornata.giornata}ª Giornata
          </div>
          {matches.map((match, index) => {
            const played = (match.score || []).length > 0;
            const homeGoals = (match.score || []).reduce(
              (t, s) => t + (s.team1 || 0),
              0
            );
            const awayGoals = (match.score || []).reduce(
              (t, s) => t + (s.team2 || 0),
              0
            );
            const homeWin = match.winnerTeam === WinnerTeam.Team1;
            const awayWin = match.winnerTeam === WinnerTeam.Team2;
            return (
              <div className="match-row" key={match.id || index}>
                <div className="match-team match-team--home">
                  <span
                    className={classNames("match-team__name", {
                      "is-winner": homeWin,
                    })}
                  >
                    {formatPair(match.home)}
                  </span>
                  {isFootball && played && (
                    <div className="match-scorers align-items-end">
                      {teamScorers(
                        match,
                        (match.home as PartialPair).id
                      )}
                    </div>
                  )}
                </div>
                <div className="match-center">
                  {played ? (
                    isFootball ? (
                      <span className="match-score">
                        <span>{homeGoals}</span>
                        <span className="match-score__sep">-</span>
                        <span>{awayGoals}</span>
                      </span>
                    ) : (
                      <span className="match-sets">
                        {(match.score || []).map((set, i) => (
                          <span key={i}>
                            <b>{set.team1}</b>-<b>{set.team2}</b>
                            {i < (match.score || []).length - 1 ? "  " : ""}
                          </span>
                        ))}
                      </span>
                    )
                  ) : (
                    <span className="match-score--empty">vs</span>
                  )}
                  {shootoutLabel(match) && (
                    <span className="match-shootout">
                      {shootoutLabel(match)}
                    </span>
                  )}
                  {match.live && (
                    <span className="live-badge">
                      <span className="live-badge__dot" />
                      LIVE
                    </span>
                  )}
                  {match.dateTime && (
                    <span className="match-dt">
                      {formatMatchDateTime(match.dateTime)}
                    </span>
                  )}
                  <button
                    className="match-edit"
                    title="Modifica risultato"
                    onClick={handleMatchModalOpen(match, gironeName)}
                  >
                    <FontAwesomeIcon icon={faPen} size="sm" />
                  </button>
                </div>
                <div className="match-team match-team--away">
                  <span
                    className={classNames("match-team__name", {
                      "is-winner": awayWin,
                    })}
                  >
                    {formatPair(match.away)}
                  </span>
                  {isFootball && played && (
                    <div className="match-scorers">
                      {teamScorers(
                        match,
                        (match.away as PartialPair).id
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {rest !== "" && (
            <div className="giornata-rest">Riposa: {rest}</div>
          )}
        </div>
      );
    },
    [isFootball, isRiposoMatch, teamScorers, handleMatchModalOpen]
  );

  const renderGironi = useCallback(() => {
    if (!tabellone) return;
    const brackets = tabellone;
    return (
      <div>
        <div className="my-4 text-center">
          <span className="tb-section-title">Classifica</span>
        </div>
        <div className="d-flex flex-column align-items-center gap-1 mb-4">
          <span className="text-muted" style={{ fontSize: 12 }}>
            Criterio a parità di punti
          </span>
          <Segmented
            value={standingsCriteria}
            onChange={(value) =>
              handleStandingsCriteriaChange(value as StandingsCriteria)
            }
            disabled={criteriaLoading}
            options={[
              { label: "Differenza", value: "difference" },
              { label: "Scontri diretti", value: "headToHead" },
            ]}
          />
          <span className="text-muted" style={{ fontSize: 11 }}>
            {isAmericano
              ? standingsCriteria === "headToHead"
                ? "Prima gli scontri diretti fra i pari punti, poi la differenza punti."
                : "Prima la differenza punti (vinti − subiti)."
              : standingsCriteria === "headToHead"
              ? isFootball
                ? "Prima gli scontri diretti, poi la differenza reti."
                : "Prima gli scontri diretti, poi differenza set e game."
              : isFootball
              ? "Prima la differenza reti, poi le reti fatte."
              : "Prima la differenza set, poi la differenza game."}
          </span>
        </div>
        {isFootball && !isBasket && (
          <div className="d-flex flex-column align-items-center gap-1 mb-4">
            <span className="text-muted" style={{ fontSize: 12 }}>
              Pareggi ai calci di rigore
            </span>
            <Segmented
              value={shootoutEnabled}
              onChange={(value) =>
                handleShootoutSettingsChange({
                  shootout: value as boolean,
                  win: shootoutWinPoints,
                  lose: shootoutLosePoints,
                })
              }
              disabled={criteriaLoading}
              options={[
                { label: "No", value: false },
                { label: "Sì", value: true },
              ]}
            />
            {shootoutEnabled ? (
              <div className="d-flex align-items-center gap-2 mt-1">
                <span className="text-muted" style={{ fontSize: 11 }}>
                  Punti: vince
                </span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  precision={0}
                  style={{ width: 64 }}
                  value={shootoutWinPoints}
                  disabled={criteriaLoading}
                  onChange={(v) =>
                    handleShootoutSettingsChange({
                      shootout: true,
                      win: v ?? 0,
                      lose: shootoutLosePoints,
                    })
                  }
                />
                <span className="text-muted" style={{ fontSize: 11 }}>
                  perde
                </span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  precision={0}
                  style={{ width: 64 }}
                  value={shootoutLosePoints}
                  disabled={criteriaLoading}
                  onChange={(v) =>
                    handleShootoutSettingsChange({
                      shootout: true,
                      win: shootoutWinPoints,
                      lose: v ?? 0,
                    })
                  }
                />
              </div>
            ) : (
              <span className="text-muted" style={{ fontSize: 11 }}>
                I pareggi valgono 1 punto a testa.
              </span>
            )}
          </div>
        )}
        <Row className="justify-content-center mx-auto w-100">
          {(brackets.roundRobin?.gironi || []).map((girone) => (
            <Col xs={12} lg={isAmericano ? 8 : 6} key={girone.name} className="mb-4">
              {isAmericano
                ? renderAmericanoStandings(girone)
                : renderStandings(girone)}
            </Col>
          ))}
        </Row>
        <div className="my-4 text-center">
          <span className="tb-section-title">Calendario</span>
        </div>
        <div className="mb-4">
          {(brackets.roundRobin?.gironi || []).map((girone) => (
            <div key={girone.name} className="mb-4">
              {!isAmericano && (
                <h6 className="text-start fw-bold text-muted mb-3">
                  Girone {girone.name}
                </h6>
              )}
              {(girone.calendario || []).map((fixture) =>
                renderGiornata(fixture, girone.name)
              )}
            </div>
          ))}
        </div>
        {!isAmericano && (
          <div className="fp-settings mb-4">
            <div className="fp-settings__title">Impostazioni fase finale</div>
            <div className="fp-settings__hint">
              Modificabili anche a gironi in corso: classifiche e calendario non
              vengono toccati, si ricostruiscono solo i tabelloni Gold/Silver.
              {goldSpots > 0 && (
                <>
                  {" "}
                  Gold: {goldQualified} qualificate su {goldSpots} posti
                  {goldSpots > goldQualified
                    ? ` (${goldSpots - goldQualified} bye al primo turno)`
                    : " (tabellone completo)"}
                  .
                </>
              )}
            </div>
            <div className="fp-settings__row">
              <div className="fp-settings__field">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Tabelloni
                </span>
                <Segmented
                  value={finalPhase.silver}
                  disabled={finalPhaseLoading}
                  onChange={(v) =>
                    setFinalPhase((p) => ({ ...p, silver: v as boolean }))
                  }
                  options={[
                    { label: "Solo Gold", value: false },
                    { label: "Gold + Silver", value: true },
                  ]}
                />
              </div>
              <div className="fp-settings__field">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  {isFootball ? "Squadre" : "Coppie"} al Gold per girone
                </span>
                <InputNumber
                  size="small"
                  min={1}
                  max={Math.max(1, finalPhase.silver ? minPerGirone - 1 : minPerGirone)}
                  precision={0}
                  style={{ width: 80 }}
                  value={finalPhase.coppieGold}
                  disabled={finalPhaseLoading}
                  onChange={(v) =>
                    setFinalPhase((p) => ({ ...p, coppieGold: v ?? 1 }))
                  }
                />
                <span className="text-muted" style={{ fontSize: 11 }}>
                  {finalPhase.silver
                    ? `le altre ${Math.max(0, minPerGirone - finalPhase.coppieGold)} al Silver`
                    : `su ${minPerGirone} per girone`}
                </span>
              </div>
              {bestExtraAvailable && (
                <div className="fp-settings__field">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Spareggio: migliori {finalPhase.coppieGold + 1}ª ripescate
                  </span>
                  <InputNumber
                    size="small"
                    min={0}
                    max={Math.max(0, gironiCount - 1)}
                    precision={0}
                    style={{ width: 80 }}
                    value={finalPhase.bestExtra}
                    disabled={finalPhaseLoading}
                    onChange={(v) =>
                      setFinalPhase((p) => ({ ...p, bestExtra: v ?? 0 }))
                    }
                  />
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    {finalPhase.bestExtra > 0
                      ? `le ${finalPhase.bestExtra} migliori ${finalPhase.coppieGold + 1}ª tra i gironi entrano nel Gold`
                      : "0 = nessun ripescaggio"}
                  </span>
                </div>
              )}
              {bestExtraAvailable && finalPhase.bestExtra > 0 && (
                <div className="fp-settings__field">
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Metodo di spareggio
                  </span>
                  <Segmented
                    value={finalPhase.bestExtraMode}
                    disabled={finalPhaseLoading}
                    onChange={(v) =>
                      setFinalPhase((p) => ({
                        ...p,
                        bestExtraMode: v as BestExtraMode,
                      }))
                    }
                    options={[
                      { label: "Contro le migliori prime", value: "vsSeeds" },
                      { label: "Tra loro", value: "amongThemselves" },
                    ]}
                  />
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    {finalPhase.bestExtraMode === "amongThemselves"
                      ? "Le ripescate si affrontano tra loro (migliore contro peggiore)"
                      : "La migliore prima incontra la peggiore ripescata, la seconda migliore prima la penultima…"}
                  </span>
                </div>
              )}
              {isFootball && (
                <div className="fp-settings__field">
                  <Checkbox
                    checked={finalPhase.thirdPlace}
                    disabled={finalPhaseLoading}
                    onChange={(e) =>
                      setFinalPhase((p) => ({
                        ...p,
                        thirdPlace: e.target.checked,
                      }))
                    }
                  >
                    Finale 3°/4° posto
                  </Checkbox>
                </div>
              )}
              <Button
                size="sm"
                variant={finalPhaseDirty ? "primary" : "light"}
                className={finalPhaseDirty ? "" : "border"}
                disabled={
                  !finalPhaseDirty ||
                  finalPhaseLoading ||
                  finalPhase.coppieGold < 1 ||
                  finalPhase.coppieGold > minPerGirone ||
                  (finalPhase.silver && finalPhase.coppieGold >= minPerGirone)
                }
                onClick={handleFinalPhaseSave}
              >
                {finalPhaseLoading ? (
                  <TailSpin color="#fff" width={15} height={15} />
                ) : (
                  "Rigenera fase finale"
                )}
              </Button>
            </div>
          </div>
        )}
        <Flex className="mb-4" justify="space-around" align="center">
          {tabellone!.goldBracket && (
            <Button
              disabled={resultLoading}
              variant="light"
              size="sm"
              className="border"
              onClick={handlePopulateBracketsFromGironi(true)}
            >
              {resultLoading ? (
                <TailSpin color="#000" width={15} height={15} />
              ) : (
                <span>Popola tabellone della fase finale Gold</span>
              )}
            </Button>
          )}
          {tabellone!.silverBracket && (
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={handlePopulateBracketsFromGironi(false, true)}
            >
              {resultLoading ? (
                <TailSpin color="#000" width={15} height={15} />
              ) : (
                <span>Popola tabellone della fase finale Silver</span>
              )}
            </Button>
          )}
        </Flex>
        <div className="mb-3 justify-content-center w-100">
          <h3 className="text-center">
            Fase Finale {brackets.silverBracket ? "Gold" : ""}
          </h3>
          <Row className="justify-content-center align-items-center">
            <Col xs={12} md={goldWinner ? 6 : 12}>
              <BracketItem
                gold
                rounds={parseGoldBracket}
                onResultClick={handleBracketModalOpen}
                onSwapEntries={handleSwapEntries("gold")}
              />
            </Col>
            {goldWinner && (
              <Col xs={12} md={6}>
                <ChampionBanner winner={goldWinner} isFootball={isFootball} />
              </Col>
            )}
          </Row>
        </div>
        {brackets.silverBracket && (
          <Row className="mb-3 justify-content-center w-100">
            <h3 className="text-center">Fase Finale Silver</h3>
            <Row className="justify-content-center align-items-center">
              <Col xs={12} md={silverWinner ? 6 : 12}>
                <BracketItem
                  silver
                  rounds={parseSilverBracket || []}
                  onResultClick={handleBracketModalOpen}
                  onSwapEntries={handleSwapEntries("silver")}
                />
              </Col>
              {silverWinner && (
                <Col xs={12} md={6}>
                  <ChampionBanner
                    winner={silverWinner}
                    isFootball={isFootball}
                  />
                </Col>
              )}
            </Row>
          </Row>
        )}
      </div>
    );
  }, [
    isBasket,
    goldWinner,
    silverWinner,
    handleBracketModalOpen,
    handlePopulateBracketsFromGironi,
    resultLoading,
    renderGiornata,
    parseGoldBracket,
    parseSilverBracket,
    tabellone,
    renderStandings,
    renderAmericanoStandings,
    isAmericano,
    isFootball,
    standingsCriteria,
    criteriaLoading,
    handleStandingsCriteriaChange,
    shootoutEnabled,
    shootoutWinPoints,
    shootoutLosePoints,
    handleShootoutSettingsChange,
    finalPhase,
    finalPhaseDirty,
    finalPhaseLoading,
    minPerGirone,
    gironiCount,
    bestExtraAvailable,
    goldQualified,
    goldSpots,
    handleFinalPhaseSave,
  ]);

  const handleLiveScoreChange = useCallback(
    (key: keyof PadelScore, index: number) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = +e.target.value;
        if (!isNaN(value)) {
          setLiveScore((prevScores) => {
            const newScores = [...prevScores];
            newScores[index] = {
              ...prevScores[index],
              [key]: value,
            };
            return newScores;
          });
        }
      },
    []
  );

  useEffect(() => {
    if (liveScore && liveScore.length > 0) {
      if (isFootball) {
        // For football, compute winner from total goals
        const totalHome = liveScore.reduce((sum, s) => sum + (s.team1 ? +s.team1 : 0), 0);
        const totalAway = liveScore.reduce((sum, s) => sum + (s.team2 ? +s.team2 : 0), 0);
        if (totalHome > totalAway) setWinner(WinnerTeam.Team1);
        else if (totalAway > totalHome) setWinner(WinnerTeam.Team2);
        else setWinner(undefined); // draw
      } else {
        let setVintiTeam1 = 0;
        let setVintiTeam2 = 0;
        liveScore.forEach((set) => {
          if (set.team1 !== undefined && set.team2 !== undefined && set.team1 !== "" && set.team2 !== "") {
            if (!isNaN(+set.team1) && !isNaN(+set.team2)) {
              if (+set.team1 > +set.team2) setVintiTeam1++;
              else if (+set.team1 < +set.team2) setVintiTeam2++;
            }
          }
        });
        if (setVintiTeam1 > setVintiTeam2) setWinner(WinnerTeam.Team1);
        else if (setVintiTeam1 < setVintiTeam2) setWinner(WinnerTeam.Team2);
        else setWinner(undefined);
      }
    }
  }, [liveScore, isFootball]);

  const renderDirectTabellone = useCallback(() => {
    if (!tabellone) return;
    const brackets = tabellone;
    if (!brackets.rounds || brackets.rounds.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessun tabellone</span>
        </Alert>
      );
    return (
      <Row className="mb-3 justify-content-center w-100">
        <Row className="justify-content-center align-items-center">
          <Col xs={12} md={roundWinner ? 6 : 12}>
            <BracketItem
              rounds={parseDirectBracket || []}
              onResultClick={handleBracketModalOpen}
              onSwapEntries={handleSwapEntries("rounds")}
            />
          </Col>
          {roundWinner && (
            <Col xs={12} md={6}>
              <ChampionBanner winner={roundWinner} isFootball={isFootball} />
            </Col>
          )}
        </Row>
      </Row>
    );
  }, [
    tabellone,
    parseDirectBracket,
    handleBracketModalOpen,
    roundWinner,
    isFootball,
  ]);

  // Classifica marcatori e assistman (solo calcetto).
  const renderFootballRankings = useCallback(() => {
    if (!isFootball) return null;
    if (topScorers.length === 0 && topAssists.length === 0) return null;
    const teamName = (teamId: string) =>
      rosterByTeamId[teamId]?.name || "";

    return (
      <div className="mt-4">
        <div className="my-3 text-center">
          <span className="tb-section-title">Classifiche</span>
        </div>
        <Row className="justify-content-center">
          <RankingCard
            title={isBasket ? "Classifica Punti" : "Classifica Marcatori"}
            icon={isBasket ? faBasketball : faFutbol}
            teamName={teamName}
            rows={topScorers.map((s) => ({
              playerName: s.playerName,
              teamId: s.teamId,
              value: s.goals,
            }))}
          />
          {!isBasket && (
            <RankingCard
              title="Classifica Assistman"
              icon={faHandshakeAngle}
              teamName={teamName}
              rows={topAssists.map((a) => ({
                playerName: a.playerName,
                teamId: a.teamId,
                value: a.assists,
              }))}
            />
          )}
        </Row>
      </div>
    );
  }, [isFootball, isBasket, topScorers, topAssists, rosterByTeamId]);

  const renderTabellone = useCallback(() => {
    return (
      <Card className="text-center justify-content-center align-items-center">
        <div className="py-3">
          <img
            src={require("../../assets/logo/logo.png")}
            alt="Get Move Logo"
            height={100}
            className="mb-3"
          />
          <h5 className="mb-1">{tabellone!.tournament.name}</h5>
          <span className="tabellone-sport-badge mt-3">
            <FontAwesomeIcon
              icon={
                isBasket
                  ? faBasketball
                  : isFootball
                  ? faFutbol
                  : faTableTennisPaddleBall
              }
              className="me-2"
            />
            {isBasket ? "Basket" : isFootball ? "Calcio" : "Padel"}
          </span>
        </div>
        {(tabellone?.gironi || []).length > 0
          ? renderGironi()
          : renderDirectTabellone()}
        {renderFootballRankings()}
      </Card>
    );
  }, [
    isBasket,
    tabellone,
    renderDirectTabellone,
    renderGironi,
    isFootball,
    renderFootballRankings,
  ]);

  const renderAsyncContent = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );
    return !tabellone ? (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    ) : (
      renderTabellone()
    );
  }, [error, renderTabellone, tabellone]);

  // Id squadra casa/ospiti della partita in modifica (vuoto se BYE/assente).
  const homeTeamId =
    match && match.home !== "BYE" ? (match.home as PartialPair).id : "";
  const awayTeamId =
    match && match.away !== "BYE" ? (match.away as PartialPair).id : "";

  // Elenco giocatori selezionabili come marcatori per una data squadra. Il
  // fallback per nome copre i tabelloni salvati con id-segnaposto (vedi sopra);
  // in quel caso gli id giocatore usano l'id REALE della squadra risolta, così
  // le statistiche restano coerenti tra le partite.
  const rosterOptions = useCallback(
    (teamId: string, fallbackName?: string): { value: string; label: string }[] => {
      const team =
        rosterByTeamId[teamId] ||
        (fallbackName ? rosterByName[fallbackName] : undefined);
      if (!team) return [];
      return (team.members || []).map((m, idx) => ({
        value: memberPlayerId(m, team.id, idx),
        label: memberFullName(m) || `Giocatore ${idx + 1}`,
      }));
    },
    [rosterByTeamId, rosterByName]
  );

  // ── Gestione punteggio calcetto (un solo "set" = gol totali) ──────────────
  const adjustGoal = useCallback((key: "team1" | "team2", delta: number) => {
    setLiveScore((prev) => {
      const next = [...prev];
      const base = next[0] || { team1: "0", team2: "0" };
      const current = Math.max(0, (base[key] ? +base[key]! : 0) + delta);
      next[0] = { ...base, [key]: String(current) };
      return next;
    });
  }, []);

  const setGoal = useCallback((key: "team1" | "team2", value: string) => {
    setLiveScore((prev) => {
      const next = [...prev];
      const base = next[0] || { team1: "", team2: "" };
      next[0] = { ...base, [key]: value };
      return next;
    });
  }, []);

  // La squadra a cui va attribuito il gol (avversaria in caso di autogol).
  const goalKeyFor = useCallback(
    (teamId: string, autogoal?: boolean): "team1" | "team2" => {
      const isHome = teamId === homeTeamId;
      const own = isHome ? "team1" : "team2";
      const opp = isHome ? "team2" : "team1";
      return autogoal ? opp : own;
    },
    [homeTeamId]
  );

  // Aggiunge un marcatore per una squadra e incrementa il relativo gol.
  const addScorer = useCallback(
    (teamId: string) => {
      // Basket: una riga per giocatore con i punti; il punteggio della
      // squadra si inserisce a parte e non viene toccato.
      setScorers((prev) => [
        ...prev,
        isBasket
          ? { playerId: "", playerName: "", teamId, points: 2 }
          : { playerId: "", playerName: "", teamId },
      ]);
      if (!isBasket) adjustGoal(goalKeyFor(teamId), 1);
    },
    [adjustGoal, goalKeyFor, isBasket]
  );

  const removeScorer = useCallback(
    (idx: number) => {
      const s = scorers[idx];
      setScorers((prev) => prev.filter((_, i) => i !== idx));
      if (s && !isBasket) adjustGoal(goalKeyFor(s.teamId, s.autogoal), -1);
    },
    [scorers, adjustGoal, goalKeyFor, isBasket]
  );

  const toggleScorerAutogoal = useCallback(
    (idx: number) => {
      const s = scorers[idx];
      if (!s) return;
      const wasAuto = s.autogoal || false;
      // Sposta il gol dalla squadra precedente a quella nuova.
      adjustGoal(goalKeyFor(s.teamId, wasAuto), -1);
      adjustGoal(goalKeyFor(s.teamId, !wasAuto), 1);
      setScorers((prev) =>
        prev.map((x, i) => (i === idx ? { ...x, autogoal: !wasAuto } : x))
      );
    },
    [scorers, adjustGoal, goalKeyFor]
  );

  const updateScorer = useCallback(
    (idx: number, patch: Partial<FootballGoal>) => {
      setScorers((prev) =>
        prev.map((x, i) => (i === idx ? { ...x, ...patch } : x))
      );
    },
    []
  );

  // ── Modale risultato calcetto: scoreboard stile partita di calcio ─────────
  const renderFootballScore = useCallback(() => {
    const g1 = (liveScore || [])[0]?.team1 ?? "";
    const g2 = (liveScore || [])[0]?.team2 ?? "";

    const renderScorerColumn = (teamId: string, side: "Casa" | "Ospiti") => {
      if (teamId === "") return null;
      const pair = side === "Casa" ? match?.home : match?.away;
      const fallbackName =
        pair && pair !== "BYE" ? (pair as PartialPair).player1Name : undefined;
      const options = rosterOptions(teamId, fallbackName);
      const rows = scorers
        .map((s, idx) => ({ s, idx }))
        .filter((x) => x.s.teamId === teamId);
      return (
        <div className="fb-scorers__col">
          <div className="fb-scorers__head">
            <FontAwesomeIcon icon={isBasket ? faBasketball : faFutbol} />
            {formatPair(pair)}
            <span className="fb-scorers__count">{rows.length}</span>
          </div>
          {rows.length === 0 && (
            <div className="fb-scorers__empty">Nessun marcatore</div>
          )}
          {rows.map(({ s, idx }) => {
            const unknownPlayer =
              s.playerId !== "" && !options.some((o) => o.value === s.playerId);
            const assistOptions = options.filter(
              (o) => o.value !== s.playerId
            );
            const unknownAssist =
              !!s.assistPlayerId &&
              !options.some((o) => o.value === s.assistPlayerId);
            return (
              <div className="fb-scorer" key={idx}>
                <div className="fb-scorer__main">
                  <select
                    className="form-select form-select-sm"
                    value={s.playerId}
                    onChange={(e) => {
                      const opt = options.find(
                        (o) => o.value === e.target.value
                      );
                      updateScorer(idx, {
                        playerId: e.target.value,
                        playerName: opt?.label ?? "",
                      });
                    }}
                  >
                    <option value="">— Giocatore —</option>
                    {unknownPlayer && (
                      <option value={s.playerId}>{s.playerName}</option>
                    )}
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {isBasket ? (
                    <Tooltip title="Punti segnati">
                      <Input
                        className="fb-scorer__min"
                        inputMode="numeric"
                        placeholder="pt"
                        value={s.points ?? ""}
                        onChange={(e) =>
                          updateScorer(idx, {
                            points: e.target.value
                              ? Math.max(0, +e.target.value || 0)
                              : undefined,
                          })
                        }
                      />
                    </Tooltip>
                  ) : (
                    <>
                      <Input
                        className="fb-scorer__min"
                        inputMode="numeric"
                        placeholder="min"
                        value={s.minute ?? ""}
                        onChange={(e) =>
                          updateScorer(idx, {
                            minute: e.target.value ? +e.target.value : undefined,
                          })
                        }
                      />
                      <Tooltip title="Autogol">
                        <label className="fb-scorer__ag">
                          <input
                            type="checkbox"
                            checked={s.autogoal || false}
                            onChange={() => toggleScorerAutogoal(idx)}
                          />
                          AG
                        </label>
                      </Tooltip>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="border-0"
                    onClick={() => removeScorer(idx)}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </Button>
                </div>
                {!isBasket && !s.autogoal && (
                  <div className="fb-scorer__assist">
                    <FontAwesomeIcon
                      icon={faHandshakeAngle}
                      className="fb-scorer__assist-icon"
                    />
                    <select
                      className="form-select form-select-sm"
                      value={s.assistPlayerId ?? ""}
                      onChange={(e) => {
                        const opt = options.find(
                          (o) => o.value === e.target.value
                        );
                        updateScorer(idx, {
                          assistPlayerId: e.target.value || undefined,
                          assistPlayerName: opt?.label,
                        });
                      }}
                    >
                      <option value="">— Assist (facoltativo) —</option>
                      {unknownAssist && (
                        <option value={s.assistPlayerId}>
                          {s.assistPlayerName}
                        </option>
                      )}
                      {assistOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            );
          })}
          <button
            type="button"
            className="fb-add-scorer"
            onClick={() => addScorer(teamId)}
          >
            <FontAwesomeIcon icon={faPlus} />{" "}
            {isBasket ? "Aggiungi giocatore" : "Aggiungi marcatore"}
          </button>
        </div>
      );
    };

    return (
      <Modal.Body>
        <div className="match-datetime">
          <span className="match-datetime__label">Data e ora partita</span>
          <input
            type="datetime-local"
            className="match-datetime__input"
            value={matchDateTime}
            onChange={(e) => setMatchDateTime(e.target.value)}
          />
        </div>
        <div className="fb-scoreboard">
          <div
            className={classNames("fb-team", {
              "fb-team--winner": winner === WinnerTeam.Team1,
            })}
          >
            <div className="fb-team__badge">{teamInitials(match?.home)}</div>
            <div className="fb-team__name">{formatPair(match?.home)}</div>
            <span className="fb-team__tag">
              {winner === WinnerTeam.Team1 && (
                <FontAwesomeIcon icon={faTrophy} className="fb-team__trophy me-1" />
              )}
              Casa
            </span>
          </div>

          <div className="fb-score">
            <div className="fb-goal">
              <button
                type="button"
                className="fb-step"
                onClick={() => adjustGoal("team1", 1)}
              >
                +
              </button>
              <input
                className="fb-goal__input"
                inputMode="numeric"
                value={g1}
                onChange={(e) =>
                  setGoal("team1", e.target.value.replace(/\D/g, ""))
                }
              />
              <button
                type="button"
                className="fb-step"
                onClick={() => adjustGoal("team1", -1)}
              >
                −
              </button>
            </div>
            <span className="fb-score__sep">:</span>
            <div className="fb-goal">
              <button
                type="button"
                className="fb-step"
                onClick={() => adjustGoal("team2", 1)}
              >
                +
              </button>
              <input
                className="fb-goal__input"
                inputMode="numeric"
                value={g2}
                onChange={(e) =>
                  setGoal("team2", e.target.value.replace(/\D/g, ""))
                }
              />
              <button
                type="button"
                className="fb-step"
                onClick={() => adjustGoal("team2", -1)}
              >
                −
              </button>
            </div>
          </div>

          <div
            className={classNames("fb-team", {
              "fb-team--winner": winner === WinnerTeam.Team2,
            })}
          >
            <div className="fb-team__badge">{teamInitials(match?.away)}</div>
            <div className="fb-team__name">{formatPair(match?.away)}</div>
            <span className="fb-team__tag">
              {winner === WinnerTeam.Team2 && (
                <FontAwesomeIcon icon={faTrophy} className="fb-team__trophy me-1" />
              )}
              Ospiti
            </span>
          </div>
        </div>

        {shootoutAvailable && (
          <div className="fb-shootout">
            <div className="fb-shootout__head">
              <FontAwesomeIcon icon={faFutbol} className="me-2" />
              Calci di rigore
              <span className="fb-shootout__hint">
                {girone
                  ? `Chi vince prende ${shootoutWinPoints} ${
                      shootoutWinPoints === 1 ? "punto" : "punti"
                    }, chi perde ${shootoutLosePoints}.`
                  : "Decidono chi passa il turno."}
              </span>
            </div>
            <div className="fb-shootout__row">
              <span className="fb-shootout__team">{formatPair(match?.home)}</span>
              <input
                className="fb-shootout__input"
                inputMode="numeric"
                value={shootoutScore.team1}
                onChange={(e) =>
                  setShootoutScore((prev) => ({
                    ...prev,
                    team1: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
              <span className="fb-shootout__sep">:</span>
              <input
                className="fb-shootout__input"
                inputMode="numeric"
                value={shootoutScore.team2}
                onChange={(e) =>
                  setShootoutScore((prev) => ({
                    ...prev,
                    team2: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
              <span className="fb-shootout__team fb-shootout__team--away">
                {formatPair(match?.away)}
              </span>
            </div>
            <div className="fb-shootout__note">
              Lascia i campi vuoti (o in parità) se la partita non è andata ai
              rigori. I gol dei rigori non contano nella differenza reti.
            </div>
          </div>
        )}

        <div className="fb-scorers">
          {footballTeams.length === 0 && (
            <div className="text-muted small">
              Le rose delle squadre non sono ancora disponibili: potrai comunque
              salvare il risultato.
            </div>
          )}
          {renderScorerColumn(homeTeamId, "Casa")}
          {renderScorerColumn(awayTeamId, "Ospiti")}
        </div>
      </Modal.Body>
    );
  }, [
    isBasket,
    liveScore,
    match,
    winner,
    scorers,
    footballTeams,
    rosterOptions,
    homeTeamId,
    awayTeamId,
    adjustGoal,
    setGoal,
    addScorer,
    removeScorer,
    toggleScorerAutogoal,
    updateScorer,
    matchDateTime,
    shootoutAvailable,
    shootoutScore,
    shootoutWinPoints,
    shootoutLosePoints,
    girone,
  ]);

  const renderLiveScore = useCallback(() => {
    if (isFootball) return renderFootballScore();
    const homeWin = winner === WinnerTeam.Team1;
    const awayWin = winner === WinnerTeam.Team2;

    // Americano: partita a punteggio singolo → una sola coppia di punti.
    if (isAmericano) {
      const s = (liveScore || [])[0] || { team1: "", team2: "" };
      const target = tabellone?.tournament?.americanoTargetPoints ?? 32;
      const winBy = tabellone?.tournament?.americanoWinBy ?? 2;
      return (
        <Modal.Body>
          <div className="match-datetime">
            <span className="match-datetime__label">Data e ora partita</span>
            <input
              type="datetime-local"
              className="match-datetime__input"
              value={matchDateTime}
              onChange={(e) => setMatchDateTime(e.target.value)}
            />
          </div>
          <div className="pd-card">
            <div
              className="text-center text-muted"
              style={{ fontSize: 12, marginBottom: 12 }}
            >
              Partita ai punti — primo a {target}, scarto di {winBy}
            </div>
            <div className={classNames("pd-row", { "pd-row--winner": homeWin })}>
              <div className="pd-row__team">
                {homeWin && (
                  <FontAwesomeIcon icon={faTrophy} className="pd-trophy" />
                )}
                <span className="pd-team-name">
                  {match?.home && match?.home !== "BYE"
                    ? formatPair(match.home)
                    : "—"}
                </span>
              </div>
              <input
                className="pd-score-input"
                inputMode="numeric"
                value={s?.team1 ?? ""}
                onChange={handleLiveScoreChange("team1", 0)}
              />
            </div>
            <div className={classNames("pd-row", { "pd-row--winner": awayWin })}>
              <div className="pd-row__team">
                {awayWin && (
                  <FontAwesomeIcon icon={faTrophy} className="pd-trophy" />
                )}
                <span className="pd-team-name">
                  {match?.away && match?.away !== "BYE"
                    ? formatPair(match.away)
                    : "—"}
                </span>
              </div>
              <input
                className="pd-score-input"
                inputMode="numeric"
                value={s?.team2 ?? ""}
                onChange={handleLiveScoreChange("team2", 0)}
              />
            </div>
          </div>
        </Modal.Body>
      );
    }

    const sets = liveScore || [];
    return (
      <Modal.Body>
        <div className="match-datetime">
          <span className="match-datetime__label">Data e ora partita</span>
          <input
            type="datetime-local"
            className="match-datetime__input"
            value={matchDateTime}
            onChange={(e) => setMatchDateTime(e.target.value)}
          />
        </div>
        <div className="pd-card">
          {/* Intestazione: etichette dei set */}
          <div className="pd-row pd-row--head">
            <div className="pd-row__team pd-row__team--head">Coppie</div>
            <div className="pd-row__sets">
              {sets.map((_s, i) => (
                <span className="pd-set-label" key={i}>
                  Set {i + 1}
                </span>
              ))}
            </div>
            <div className="pd-row__actions">
              <Tooltip title="Aggiungi Set">
                <button className="pd-set-btn" onClick={handleAddSet}>
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </Tooltip>
              {sets.length > 1 && (
                <Tooltip title="Elimina Set">
                  <button className="pd-set-btn" onClick={handleRemoveSet}>
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Riga coppia 1 */}
          <div className={classNames("pd-row", { "pd-row--winner": homeWin })}>
            <div className="pd-row__team">
              {homeWin && (
                <FontAwesomeIcon icon={faTrophy} className="pd-trophy" />
              )}
              <span className="pd-team-name">
                {!match?.home ? (
                  <TailSpin color="#f25d0b" width={18} height={18} />
                ) : (
                  <>{match?.home !== "BYE" ? formatPair(match?.home) : "—"}</>
                )}
              </span>
            </div>
            <div className="pd-row__sets">
              {sets.map((s, i) => (
                <input
                  key={i}
                  className="pd-score-input"
                  inputMode="numeric"
                  value={s?.team1 ?? ""}
                  onChange={handleLiveScoreChange("team1", i)}
                />
              ))}
            </div>
            <div className="pd-row__actions" />
          </div>

          {/* Riga coppia 2 */}
          <div className={classNames("pd-row", { "pd-row--winner": awayWin })}>
            <div className="pd-row__team">
              {awayWin && (
                <FontAwesomeIcon icon={faTrophy} className="pd-trophy" />
              )}
              <span className="pd-team-name">
                {!match?.away ? (
                  <TailSpin color="#f25d0b" width={18} height={18} />
                ) : (
                  <>{match?.away !== "BYE" ? formatPair(match?.away) : "—"}</>
                )}
              </span>
            </div>
            <div className="pd-row__sets">
              {sets.map((s, i) => (
                <input
                  key={i}
                  className="pd-score-input"
                  inputMode="numeric"
                  value={s?.team2 ?? ""}
                  onChange={handleLiveScoreChange("team2", i)}
                />
              ))}
            </div>
            <div className="pd-row__actions" />
          </div>
        </div>
      </Modal.Body>
    );
  }, [
    liveScore,
    handleAddSet,
    handleLiveScoreChange,
    handleRemoveSet,
    match,
    winner,
    isFootball,
    isAmericano,
    tabellone,
    renderFootballScore,
    matchDateTime,
    setMatchDateTime,
  ]);

  useEffect(() => {
    if (!tabellone) return;
    // Il campione è chi vince la vera finale: la finale 3°/4° posto è appesa in
    // coda ai turni e va esclusa (altrimenti il campione sarebbe il suo vincitore).
    if (tabellone.goldBracket)
      setGoldWinner(finalWinnerOf(tabellone.goldBracket));
    if (tabellone.silverBracket)
      setSilverWinner(finalWinnerOf(tabellone.silverBracket));
    if (tabellone.rounds) setRoundWinner(finalWinnerOf(tabellone.rounds));
  }, [tabellone]);

  const handleResultSave = useCallback(() => {
    // Basket: niente pareggi (si va ai supplementari).
    if (isBasket) {
      const h = +((liveScore || [])[0]?.team1 || 0);
      const a = +((liveScore || [])[0]?.team2 || 0);
      if (h === a) {
        showToast(
          "result-tie",
          "Nel basket non sono ammessi pareggi",
          "Inserisci il punteggio dopo gli eventuali supplementari.",
          "danger"
        );
        return;
      }
    }
    setResultLoading(true);
    // Finale del tabellone attivo, escludendo la finale 3°/4° posto.
    const activeBracket = gold
      ? tabellone!.goldBracket
      : silver
      ? tabellone!.silverBracket
      : tabellone!.rounds;
    const finalMatch = finalMatchOf(activeBracket);
    Tournaments.updateTabelloneResult(id!, {
      matchId: match!.id,
      girone,
      score: liveScore!.map((sc) => ({ team1: +sc.team1!, team2: +sc.team2! })),
      winnerTeam: winner,
      gold,
      silver,
      football: isFootball || undefined,
      // Stringa vuota = rimuovi la data/ora; valore = imposta.
      dateTime: matchDateTime,
      // Invia sempre l'array per il calcetto (anche vuoto) così da poter
      // cancellare i marcatori; scarta le righe senza giocatore selezionato.
      scorers: isBasket
        ? scorers
            .filter((s) => s.playerId !== "" && s.playerName !== "")
            .map((s) => ({
              playerId: s.playerId,
              playerName: s.playerName,
              teamId: s.teamId,
              points: Math.max(0, +(s.points ?? 0) || 0),
            }))
        : isFootball
        ? scorers.filter((s) => s.playerId !== "" && s.playerName !== "")
        : undefined,
      // Rigori: inviati solo su un pareggio con entrambi i punteggi valorizzati.
      shootout:
        shootoutAvailable &&
        shootoutScore.team1 !== "" &&
        shootoutScore.team2 !== ""
          ? { team1: +shootoutScore.team1, team2: +shootoutScore.team2 }
          : undefined,
    }).then(
      () => {
        handleReset();
        setResultLoading(false);
        showToast(
          "result-success",
          "Risultato correttamente aggiornato",
          "",
          "success"
        );
        const winnerTeam =
          match!.winnerTeam === WinnerTeam.Team1 ? match!.home : match!.away;
        const isFinal = !!finalMatch && finalMatch.id === match!.id;
        if (isFinal) {
          if (gold) setGoldWinner(winnerTeam as PartialPair);
          else if (silver) setSilverWinner(winnerTeam as PartialPair);
          else setRoundWinner(winnerTeam as PartialPair);
        }
      },
      () => {
        setResultLoading(false);
        showToast(
          "result-error",
          "Errore nell'aggiornamento del risultato",
          "",
          "danger"
        );
      }
    );
  }, [
    isBasket,
    tabellone,
    id,
    match,
    liveScore,
    winner,
    girone,
    showToast,
    handleReset,
    gold,
    silver,
    isFootball,
    scorers,
    matchDateTime,
    shootoutAvailable,
    shootoutScore,
  ]);

  // Aggiorna SOLO la data/ora, senza modificare il risultato.
  const handleSaveDateOnly = useCallback(() => {
    setResultLoading(true);
    Tournaments.updateTabelloneResult(id!, {
      matchId: match!.id,
      girone,
      gold,
      silver,
      dateOnly: true,
      dateTime: matchDateTime,
      score: [],
    }).then(
      () => {
        handleReset();
        setResultLoading(false);
        showToast("date-success", "Data partita aggiornata", "", "success");
      },
      () => {
        setResultLoading(false);
        showToast(
          "date-error",
          "Errore nell'aggiornamento della data",
          "",
          "danger"
        );
      }
    );
  }, [id, match, girone, gold, silver, matchDateTime, handleReset, showToast]);

  // Azzera il risultato: la partita torna "non giocata".
  const handleResetResult = useCallback(() => {
    setResultLoading(true);
    Tournaments.updateTabelloneResult(id!, {
      matchId: match!.id,
      girone,
      gold,
      silver,
      football: isFootball || undefined,
      reset: true,
      score: [],
    }).then(
      () => {
        handleReset();
        setResultLoading(false);
        showToast(
          "reset-success",
          "Risultato azzerato: partita non giocata",
          "",
          "success"
        );
      },
      () => {
        setResultLoading(false);
        showToast(
          "reset-error",
          "Errore nell'azzeramento del risultato",
          "",
          "danger"
        );
      }
    );
  }, [id, match, girone, gold, silver, isFootball, handleReset, showToast]);

  // Accende/spegne lo stato "LIVE" della partita (true = LIVE, false = termina).
  const handleToggleLive = useCallback(
    (next: boolean) => {
      setResultLoading(true);
      Tournaments.updateTabelloneResult(id!, {
        matchId: match!.id,
        girone,
        gold,
        silver,
        football: isFootball || undefined,
        live: next,
        score: [],
      }).then(
        () => {
          handleReset();
          setResultLoading(false);
          showToast(
            "live-success",
            next ? "Partita segnata come LIVE" : "Partita terminata",
            "",
            "success"
          );
        },
        () => {
          setResultLoading(false);
          showToast(
            "live-error",
            "Errore nell'aggiornamento dello stato LIVE",
            "",
            "danger"
          );
        }
      );
    },
    [id, match, girone, gold, silver, isFootball, handleReset, showToast]
  );

  return (
    <Container className="my-3">
      <Helmet>
        <title>{`${tabellone?.tournament.name} - Tabellone`}</title>
      </Helmet>

      <Row className="mb-2">
        <Col md={8} xs={12}>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tornei" }}>
              Tornei
            </Breadcrumb.Item>
            {tabellone ? (
              <>
                <Breadcrumb.Item
                  linkAs={Link}
                  linkProps={{ to: `/tornei/${id}` }}
                >
                  {tabellone.tournament.name}
                </Breadcrumb.Item>
              </>
            ) : (
              <Breadcrumb.Item>
                <TailSpin color="#f25d0b" width={20} height={20} />
              </Breadcrumb.Item>
            )}
            <Breadcrumb.Item active>Tabellone</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
        <Col md={4} xs={12} className="text-end">
          <Button size="sm" variant="success" onClick={handleDownloadPdf}>
            <FontAwesomeIcon icon={faPrint} className="me-2" />
            Stampa Tabellone
          </Button>
        </Col>
      </Row>

      <Row className="g-3" ref={ref}>
        {renderAsyncContent()}
      </Row>

      <Modal show={matchModalOpen} onHide={handleMatchModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Modifica risultato</Modal.Title>
        </Modal.Header>
        {renderLiveScore()}
        <Modal.Footer>
          {(match?.score || []).length > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              className="me-auto"
              disabled={resultLoading}
              onClick={handleResetResult}
            >
              <FontAwesomeIcon icon={faRotateLeft} className="me-2" />
              Resetta partita
            </Button>
          )}
          <Button
            variant={match?.live ? "danger" : "outline-danger"}
            size="sm"
            className={(match?.score || []).length > 0 ? "" : "me-auto"}
            disabled={resultLoading}
            onClick={() => handleToggleLive(!match?.live)}
          >
            <FontAwesomeIcon icon={faTowerBroadcast} className="me-2" />
            {match?.live ? "Termina partita" : "Segna LIVE"}
          </Button>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleMatchModalClose}
          >
            Chiudi
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={resultLoading}
            onClick={handleSaveDateOnly}
          >
            <FontAwesomeIcon icon={faClock} className="me-2" />
            Salva solo data
          </Button>
          <Button
            disabled={(liveScore.length === 0) || resultLoading}
            variant="success"
            size="sm"
            onClick={handleResultSave}
          >
            {resultLoading ? (
              <TailSpin color="#000" width={15} height={15} />
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                <span className="ms-2">Salva Risultato</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
